import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { MessageCircle } from 'lucide-react'
import { z } from 'zod'
import { Button, Input, Card } from '../ui'
import { useAuthStore } from '../../stores'
import { createRecaptchaVerifier } from '../../lib/firebase'
import SocialLoginButtons from './SocialLoginButtons'

const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'El número de teléfono es obligatorio')
    .min(8, 'El número debe tener al menos 8 dígitos')
    .max(15, 'El número no puede tener más de 15 dígitos')
    .regex(/^[0-9]+$/, 'Solo se permiten números')
})

const codeSchema = z.object({
  verificationCode: z
    .string()
    .min(6, 'El código debe tener 6 dígitos')
    .max(6, 'El código debe tener 6 dígitos')
    .regex(/^\d{6}$/, 'El código debe contener solo números')
})

type PhoneFormData = z.infer<typeof phoneSchema>
type CodeFormData = z.infer<typeof codeSchema>

interface Country {
  code: string
  name: string
  prefix: string
  flag: string
}

const countries: Country[] = [
  { code: 'ES', name: 'España', prefix: '+34', flag: '🇪🇸' },
  { code: 'FR', name: 'Francia', prefix: '+33', flag: '🇫🇷' },
  { code: 'IT', name: 'Italia', prefix: '+39', flag: '🇮🇹' },
  { code: 'PT', name: 'Portugal', prefix: '+351', flag: '🇵🇹' },
  { code: 'GB', name: 'Reino Unido', prefix: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Alemania', prefix: '+49', flag: '🇩🇪' },
  { code: 'US', name: 'Estados Unidos', prefix: '+1', flag: '🇺🇸' },
  { code: 'MX', name: 'México', prefix: '+52', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', prefix: '+54', flag: '🇦🇷' },
  { code: 'CO', name: 'Colombia', prefix: '+57', flag: '🇨🇴' }
]


interface PhoneSignInFormProps {}

const PhoneSignInForm = ({}: PhoneSignInFormProps) => {
  const [step, setStep] = useState<'phone' | 'code'>('phone')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]) // España por defecto
  const recaptchaRef = useRef<HTMLDivElement>(null)
  const recaptchaVerifierRef = useRef<any>(null)

  const { signInWithPhone, verifyPhoneCode, isLoading, error, clearError } = useAuthStore()

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema)
  })

  const codeForm = useForm<CodeFormData>({
    resolver: zodResolver(codeSchema)
  })


  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    const initReCaptcha = async () => {
      // Wait for grecaptcha to be available
      if (typeof window === 'undefined' || !(window as any).grecaptcha) {
        setTimeout(initReCaptcha, 500)
        return
      }

      const container = document.getElementById('recaptcha-container')

      if (container && !recaptchaVerifierRef.current) {
        try {
          container.innerHTML = ''
          recaptchaVerifierRef.current = createRecaptchaVerifier('recaptcha-container')
        } catch (error) {
          console.error('Error initializing reCAPTCHA verifier:', error)
        }
      } else if (!container) {
        console.error('reCAPTCHA container element not found in DOM')
      }
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initReCaptcha, 500)

    // Cleanup on unmount
    return () => {
      clearTimeout(timer)
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear()
        } catch (e) {
          console.warn('Error clearing reCAPTCHA:', e)
        }
        recaptchaVerifierRef.current = null
      }
    }
  }, [])

  const onPhoneSubmit = async (data: PhoneFormData) => {
    if (!recaptchaVerifierRef.current) {
      console.error('reCAPTCHA not initialized')
      // Try to reinitialize
      try {
        const container = document.getElementById('recaptcha-container')
        if (container) {
          container.innerHTML = ''
          recaptchaVerifierRef.current = createRecaptchaVerifier('recaptcha-container')
        }
      } catch (e) {
        console.error('Failed to reinitialize reCAPTCHA:', e)
        return
      }
    }

    clearError()
    const fullPhoneNumber = selectedCountry.prefix + data.phoneNumber
    setPhoneNumber(fullPhoneNumber)

    await signInWithPhone(fullPhoneNumber, recaptchaVerifierRef.current)

    // Only proceed to code step if operation was successful
    // Check if there's an error in the store after the operation
    const storeState = useAuthStore.getState()
    if (!storeState.error && storeState.phoneConfirmationResult) {
      setStep('code')
    } else {
      // Reset reCAPTCHA on error
      try {
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear()
        }
        const container = document.getElementById('recaptcha-container')
        if (container) {
          container.innerHTML = ''
          recaptchaVerifierRef.current = createRecaptchaVerifier('recaptcha-container')
        }
      } catch (e) {
        console.warn('Error resetting reCAPTCHA:', e)
      }
    }
  }

  const onCodeSubmit = async (data: CodeFormData) => {
    clearError()

    try {
      await verifyPhoneCode(data.verificationCode)
    } catch (error) {
      console.error('Error verifying code:', error)
    }
  }

  const handleBackToPhone = () => {
    setStep('phone')
    codeForm.reset()
    // Reset reCAPTCHA
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear()
      }
      const container = document.getElementById('recaptcha-container')
      if (container) {
        container.innerHTML = ''
        recaptchaVerifierRef.current = createRecaptchaVerifier('recaptcha-container')
      }
    } catch (e) {
      console.warn('Error resetting reCAPTCHA on back:', e)
    }
  }

  if (step === 'code') {
    return (
      <div>
        <Card className="w-full mx-auto" hover={false}>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Verificación SMS
            </h2>
            <p className="text-gray-600">
              Introduce el código de 6 dígitos enviado a <br />
              <strong>{phoneNumber}</strong>
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={codeForm.handleSubmit(onCodeSubmit)} className="space-y-4">
            <Input
              {...codeForm.register('verificationCode')}
              type="text"
              label="Código de verificación"
              placeholder="123456"
              error={codeForm.formState.errors.verificationCode?.message}
              leftIcon={<MessageCircle className="w-5 h-5" />}
              autoComplete="one-time-code"
              maxLength={6}
              disabled={isLoading}
              className="text-center text-lg tracking-widest"
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              isLoading={isLoading}
            >
              Verificar código
            </Button>

            <Button
              type="button"
              onClick={handleBackToPhone}
              variant="ghost"
              className="w-full"
              disabled={isLoading}
            >
              Cambiar número de teléfono
            </Button>
          </form>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <Card className="w-full mx-auto" hover={false}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Iniciar Sesión
          </h2>
          <p className="text-gray-600">
            Introduce tu número de teléfono para acceder
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="phone-group" className="block text-sm font-medium text-gray-700">
              Número de teléfono
            </label>
            <div
              id="phone-group"
              className="flex flex-col sm:flex-row gap-2 sm:gap-0"
              role="group"
              aria-labelledby="phone-group-label"
              aria-describedby={phoneForm.formState.errors.phoneNumber ? 'phone-error' : 'phone-help'}
            >
              <label id="phone-group-label" className="sr-only">
                Número de teléfono completo con código de país
              </label>
              <select
                id="country-select"
                value={selectedCountry.code}
                onChange={(e) => {
                  const country = countries.find(c => c.code === e.target.value)
                  if (country) setSelectedCountry(country)
                }}
                className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg sm:rounded-r-none sm:border-r-0 sm:rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px] sm:min-w-[100px] text-sm flex-shrink-0"
                disabled={isLoading}
                aria-label="Seleccionar código de país"
                aria-describedby="country-help"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.prefix}
                  </option>
                ))}
              </select>
              <input
                {...phoneForm.register('phoneNumber')}
                id="phone-number-input"
                type="tel"
                placeholder={selectedCountry.code === 'ES' ? '612345678' : 'Número de teléfono'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg sm:rounded-l-none sm:rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0"
                autoComplete="tel"
                disabled={isLoading}
                aria-label="Número de teléfono"
                aria-invalid={!!phoneForm.formState.errors.phoneNumber}
                aria-describedby={phoneForm.formState.errors.phoneNumber ? 'phone-error' : 'phone-help'}
              />
            </div>
            <div id="phone-help" className="sr-only">
              Introduce tu número de teléfono sin el código de país
            </div>
            <div id="country-help" className="sr-only">
              Selecciona el código de país para tu número de teléfono
            </div>
            {phoneForm.formState.errors.phoneNumber && (
              <div
                id="phone-error"
                role="alert"
                aria-live="polite"
                className="text-sm text-red-600"
              >
                {phoneForm.formState.errors.phoneNumber.message}
              </div>
            )}
          </div>

          {/* reCAPTCHA container */}
          <div id="recaptcha-container" ref={recaptchaRef} className="flex justify-center"></div>


          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={isLoading}
          >
            Enviar código SMS
          </Button>
        </form>

        <SocialLoginButtons disabled={isLoading} />
      </Card>
    </div>
  )
}

export default PhoneSignInForm